import React from 'react';
import { Tag, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

/**
 * How much of the room a candidate actually used, printed beside their score.
 *
 * THIS IS AN OUTCOME, NOT A COMPLAINT. Pillar 1 — news mapping and conviction
 * — is a third of the official composite and is earned entirely on the
 * conviction sliders and the news-assignment panel: no trade is required for
 * it. So a candidate who filled that surface in and never placed an order has
 * a real mark, and the mark has to say what it was earned on. Before this
 * marker existed, that person was simply missing from the report, and the
 * reader had no way to tell "did not sit" from "sat and did not trade".
 *
 * The three values come from the grading record (`AssessmentResult.participation`):
 *   traded      — placed at least one order or currency exchange
 *   voted_only  — conviction sliders and/or news assignments, no trade
 *   absent      — nothing at all
 *
 * NEVER A WARNING COLOUR. Orange means "something is wrong with this score";
 * nothing is wrong with this score. The marker is secondary text, or a default
 * tag inside a table, and it reads the same for every role — an administrator,
 * a trainer, a client observer and the candidate's own report all get the same
 * sentence, because it is a fact about the sitting rather than an explanation
 * of it.
 *
 * A score computed before the flag existed carries no value; the marker then
 * renders NOTHING rather than guessing "traded".
 */

export const TRADED_LABEL = 'Traded';
export const VOTED_ONLY_LABEL = 'Voted only — no trades placed';
export const ABSENT_LABEL = 'Did not take part';

const LABELS: Record<string, string> = {
  traded: TRADED_LABEL,
  voted_only: VOTED_ONLY_LABEL,
  absent: ABSENT_LABEL,
};

/** The sentence for a flag, or '' when there is nothing honest to say. */
export const participationLabel = (participation?: string | null): string =>
  (participation && LABELS[participation]) || '';

export const ParticipationNote: React.FC<{
  participation?: string | null;
  /** Render as an antd Tag (tables) rather than plain secondary text. */
  asTag?: boolean;
  className?: string;
}> = ({ participation, asTag, className }) => {
  const label = participationLabel(participation);
  if (!label) return null;
  return (
    <span data-participation={participation} className={className}>
      {asTag ? (
        <Tag color="default">{label}</Tag>
      ) : (
        <Typography.Text type="secondary">{label}</Typography.Text>
      )}
    </span>
  );
};

export default ParticipationNote;

/**
 * How much of the conviction-slider surface a score stands on, printed beside
 * it when that is under half.
 *
 * Every candidate is graded (the slider evidence gate was lifted 2026-10-09:
 * on prod it withheld every real trainee's grade, and the screens read that as
 * "results pending" forever). An unstated conviction view earns zero credit, so
 * a trader who rarely sets the sliders gets a real but low pillar 1, and the
 * reader needs to know why. Like `ParticipationNote`, it is an OUTCOME fact
 * and reads the same for every role.
 *
 * `coverage` is `AssessmentResult.conviction_coverage`: the share (0..1) of
 * period × conviction-instrument cells with an explicit human view. Absent or
 * null renders nothing.
 */

/** Mirrors `SLIDER_CAPTURE_FLOOR` in pm.api `pm/assessment/engine/adapters.py`. */
export const CONVICTION_RELIABLE_FROM = 0.5;

const coveragePercent = (coverage: number): string => {
  const percent = Math.round(coverage * 100);
  return percent === 0 && coverage > 0 ? 'less than 1%' : `${percent}%`;
};

/** True when the note has something to say. */
export const isLowConvictionCoverage = (
  coverage?: number | null,
): coverage is number =>
  typeof coverage === 'number' && coverage < CONVICTION_RELIABLE_FROM;

/** The full sentence, or '' when coverage is unknown or at least half. */
export const convictionCoverageSentence = (
  coverage?: number | null,
): string => {
  if (!isLowConvictionCoverage(coverage)) return '';
  if (coverage <= 0)
    return (
      'No conviction views were set in this session. Scores are most ' +
      "reliable when views cover at least half of the session's periods " +
      'and instruments.'
    );
  return (
    `Conviction views were set for only ${coveragePercent(coverage)} of ` +
    "this session's periods and instruments. Scores are most reliable when " +
    'views cover at least half.'
  );
};

/** The short label every surface prints: "No conviction views set" or
 *  "Conviction views set: 3%". The full sentence rides in its tooltip. */
export const convictionCoverageShort = (coverage?: number | null): string => {
  if (!isLowConvictionCoverage(coverage)) return '';
  return coverage <= 0
    ? 'No conviction views set'
    : `Conviction views set: ${coveragePercent(coverage)}`;
};

export const ConvictionCoverageNote: React.FC<{
  coverage?: number | null;
  /** Render as an antd Tag (tables) rather than secondary text. */
  asTag?: boolean;
  className?: string;
}> = ({ coverage, asTag, className }) => {
  const sentence = convictionCoverageSentence(coverage);
  if (!sentence) return null;
  const label = convictionCoverageShort(coverage);
  return (
    <span data-conviction-coverage={coverage} className={className}>
      {asTag ? (
        <Tooltip title={sentence}>
          <Tag color="default">{label}</Tag>
        </Tooltip>
      ) : (
        // Short fact inline; the why is one hover or one Tab away, so the
        // line never turns into a paragraph beside a score.
        <Typography.Text type="secondary">
          {label}
          <Tooltip title={sentence}>
            <InfoCircleOutlined
              tabIndex={0}
              aria-label={sentence}
              style={{ marginLeft: 5, cursor: 'help' }}
            />
          </Tooltip>
        </Typography.Text>
      )}
    </span>
  );
};
